/**
 * P198 -- live-telemetry states for capability cells.
 *
 * Telemetry is per-SERVICE; a capmap cell is a CAPABILITY. We resolve a
 * cell's live status as the WORST telemetry across the services that
 * support the IBSes the capability is linked to:
 *
 *   capability --linked_ibs_ids--> IBS --(twin graph IBS<->service edge)-->
 *   service --telemetry--> status   (take the worst)
 *
 * Pure + testable. Cells with no resolvable service fall to "stable".
 */
import type { CapabilityCell, ServiceHealth, TwinEdge } from "shared/api/types";
import type { CapabilityState } from "twin/capmap/stateMap";

type LiveStatus = ServiceHealth["status"];

const RANK: Record<LiveStatus, number> = {
  down: 3,
  degraded: 2,
  healthy: 1,
  unknown: 0,
  // C5 -- never monitored ranks with "no signal", not with trouble.
  // A service nobody is watching must not tint a capability as
  // though something were wrong with it: the absence of a reading is
  // not evidence of a fault.
  no_data: 0,
};

export function buildLiveStates(
  cells: CapabilityCell[],
  twinEdges: TwinEdge[],
  telemetry: ServiceHealth[],
): Map<string, CapabilityState> {
  // IBS id -> set of supporting service ids (either edge direction).
  const ibsToServices = new Map<string, Set<string>>();
  for (const e of twinEdges) {
    let ibs: string | null = null;
    let svc: string | null = null;
    if (e.from_kind === "ibs" && e.to_kind === "service") {
      ibs = e.from_id;
      svc = e.to_id;
    } else if (e.from_kind === "service" && e.to_kind === "ibs") {
      ibs = e.to_id;
      svc = e.from_id;
    }
    if (ibs && svc) {
      if (!ibsToServices.has(ibs)) ibsToServices.set(ibs, new Set());
      ibsToServices.get(ibs)!.add(svc);
    }
  }

  const status = new Map<string, LiveStatus>();
  for (const r of telemetry) status.set(r.service_id, r.status);

  const out = new Map<string, CapabilityState>();
  for (const cell of cells) {
    let worst = 0;
    for (const ibsId of cell.linked_ibs_ids) {
      for (const svcId of ibsToServices.get(ibsId) ?? []) {
        const s = status.get(svcId);
        if (s !== undefined && RANK[s] > worst) worst = RANK[s];
      }
    }
    // Only down/degraded tint the cell; healthy/unknown/none stay calm.
    out.set(cell.id, worst === 3 ? "down" : worst === 2 ? "degraded" : "stable");
  }
  return out;
}
